import React, { useState } from 'react';
import PlaceCard from './placeCard';
import Loader from './../../common/loader';
import LocationControls from './LocationControls';
import useGeolocation from '../../hooks/useGeoLocation';
import { searchPlaces } from '../../services/places';
import CountryDropDown from '../../components/user/CountryDropDown';

export default function PlacesSearch() {
  const [query, setQuery] = useState('');
  const [radius, setRadius] = useState(100000); // Default 100 km
  const [isSearching, setIsSearching] = useState(false);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  
  const {
    location,
    error: locationError,
    isLoading: isLocating,
    getCurrentLocation
  } = useGeolocation();

  const handleSearch = async (e, isLoadMore = false) => {
    if (e) e.preventDefault();
    if (!query.trim() && !isLoadMore) return;
    
    setIsSearching(true);
    setError(null);
    
    try {
      const {data} = await searchPlaces({
        textQuery: query,
        lat: location.lat,
        long: location.lng,
        radius,
        pageToken: isLoadMore ? nextPageToken : undefined
      });
      const {places,nextPageToken: newToken,hasMore} = data;
      console.log("places",places);
      if (isLoadMore) {
        // Append new results to existing ones
        setResults(prev => [...prev, ...(places || [])]);
      } else {
        // New search, replace existing results
        setResults(places || []);
      }
      setNextPageToken(newToken);
      setHasMore(hasMore);
    } catch (err) {
      setError(err.message);
      if (!isLoadMore) setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleLoadMore = () => {
    handleSearch(null, true);
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Business Locator
          </h1>
          <p className="text-gray-600">
            Find businesses near you using Google Places API
          </p>
        </div>
        
        <LocationControls 
          radius={radius}
          setRadius={setRadius}
          location={location}
          locationError={locationError}
          isLocating={isLocating}
          getCurrentLocation={getCurrentLocation}
        />
        
        <form onSubmit={handleSearch} className="mb-8">
          <div className="flex gap-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for businesses, places..."
              className="flex-grow p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isSearching}
            />
            <button
              type="submit"
              disabled={!query || isSearching}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>
          
          <div className="mt-2 text-sm text-gray-500">
            Try searches like "restaurants", "hotels", or "malls"
          </div>
        </form>
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
            <p className="font-bold">Error:</p>
            <p>{error}</p>
          </div>
        )}
        
        {isSearching && <Loader />}
        
        {!isSearching && results.length > 0 && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Search Results</h2>
              <span className="text-sm text-gray-500">
                {results.length} businesses found within {radius/1000} km
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.map((place, index) => (
                <PlaceCard key={`${place.id}-${index}`} place={place} />
              ))}
            </div>
          </div>
        )}
        
        {!isSearching && results.length === 0 && query && (
          <div className="text-center py-8 text-gray-500">
            No results found for "{query}" within {radius/1000} km
          </div>
        )}
        {hasMore && !isSearching && (
          <div className="text-center mt-6">
               <button
              onClick={handleLoadMore}
              disabled={isSearching}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isSearching ? 'Loading...' : 'Load More Results'}
            </button>
          </div>
        )}
      </div>
      
    </div>
  );
}
